import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, AlertTitle, Box, Button, Chip, CircularProgress, FormControl, FormHelperText, Grid, InputLabel,
    MenuItem, Paper, Select, TextField, Typography
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { observer } from 'mobx-react-lite';
import audioSettingsStore from '../../Mobx/AudioSettingsStore';
import generateAudioStore from '../../Mobx/GenerateAudioStore';
import {
    AVERAGE_CHARS_PER_FILE, INWORLD_DELIVERY_MODES, INWORLD_KEY_NOTE, INWORLD_NIQQUD_MODES, LANGUAGE_INFO, LANGUAGE_KEYS,
    formatError
} from '../../Mobx/AudioCatalog';
import { patchPaths } from './audioSettingsForm';
import {
    buildInworldPatch, estimateCostPerThousandFiles, formFromInworldView, inworldModelsOf, isInworldFormChanged,
    isSpeakingRateValid, parseSpeakingRate, parseVoices, speakingRateRangeOf, validateInworldForm, voiceFieldProblem,
    voiceLimitsOf
} from './inworldSettingsForm';
import useServerForm from './useServerForm';

/**
 * The settings of Inworld for the automatic creation of the audio files: the model, the pace, the delivery, the niqqud and the voices
 * of each language and gender. What is saved here is what the server uses for every cell of the rules whose provider is Inworld
 * (ai_settings.audio.speech.inworld). The "בדיקה" buttons create one real audio file with the values that are in the form
 * (even before they are saved).
 */

const SAVED_MESSAGE_MS = 6000;
const MAX_SAMPLE_CHARS = 200;

const DELIVERY_MODE_INFO = {
    STABLE: 'STABLE - יציב ועקבי (ברירת המחדל, מתאים למילים בודדות)',
    BALANCED: 'BALANCED - מאוזן',
    CREATIVE: 'CREATIVE - אקספרסיבי יותר ופחות עקבי',
};

const NIQQUD_INFO = {
    keep: 'להשאיר את הניקוד כפי שהוא כתוב במילון',
    strip: 'להסיר את הניקוד לפני ההקראה',
};

const MODEL_NOTE = 'Inworld מחזיר קובץ MP3 ישירות. אין לו פרומפט של סגנון: את אופן הקריאה קובעים הקול, המהירות ומצב ההגשה. '
    + 'החיוב הוא לפי מספר התווים שנשלחים, ואין מכסה יומית. מספר הבקשות במקביל מוגבל לפי התוכנית בחשבון Inworld '
    + '(לפי התיעוד ב-1.11.2026: ב-On-Demand 5, ב-Creator 10), והשרת שולח עד 4 במקביל; בקשה מעל המגבלה מקבלת שגיאה 429 והשרת מנסה שוב.';

const formatUsd = (value) => `$${value.toFixed(2)}`;

// ____________________________________________________________________________________________

/**
 * The voices of one language and one gender: the field (the voice ids, separated by commas) and a "test" button for each voice.
 * With several voices every word gets one of them, chosen by the name of its file - so a word always has the same voice.
 */
const VoiceCell = ({ id, label, value, onChange, inworldView, blockedReason, buildQuery }) => {
    const [state, setState] = useState({ status: 'idle' });
    const { max, pattern } = voiceLimitsOf(inworldView);
    const voices = parseVoices(value);
    const problem = voiceFieldProblem(value, inworldView);

    const run = async (voice) => {
        setState({ status: 'loading', voice });
        const query = buildQuery(voice);
        const { sample, error } = await generateAudioStore.requestAudio(query);
        setState(error
            ? { status: 'error', voice, error }
            : { status: 'ready', voice, sample, caption: `${query.model} · ${voice} · ${(sample.ms / 1000).toFixed(1)} s` });
    };

    let helperText = 'קול אחד, או כמה קולות מופרדים בפסיק.';
    if (problem === 'count') helperText = `צריך בין 1 ל-${max} קולות, מופרדים בפסיק.`;
    else if (problem === 'pattern') helperText = 'מזהה קול יכול להכיל אותיות באנגלית, ספרות ו-. _ : - בלבד.';
    else if (voices.length > 1) helperText = `${voices.length} קולות: כל מילה מקבלת אחד מהם, ותמיד אותו קול.`;

    return (
        <Box>
            <TextField
                id={id}
                label={label}
                size="small"
                fullWidth
                value={value}
                onChange={(e) => onChange(e.target.value)}
                error={Boolean(problem)}
                helperText={helperText}
                inputProps={{ dir: 'ltr', spellCheck: false }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {voices.slice(0, max).filter((voice) => pattern.test(voice)).map((voice) => (
                    <Button
                        key={voice}
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: 'none' }}
                        onClick={() => run(voice)}
                        disabled={Boolean(blockedReason) || state.status === 'loading'}
                        startIcon={state.status === 'loading' && state.voice === voice ? <CircularProgress size={14} /> : <PlayArrowIcon />}
                    >
                        בדיקה: <bdi>{voice}</bdi>
                    </Button>
                ))}
            </Box>
            {state.status === 'ready' && (
                <Box sx={{ mt: 0.5 }}>
                    <audio controls autoPlay src={state.sample.url} style={{ width: '100%', height: 36 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}><bdi>{state.caption}</bdi></Typography>
                </Box>
            )}
            {state.status === 'error' && (
                <Typography variant="caption" color="error" dir="auto" sx={{ display: 'block', mt: 0.5 }}>
                    <bdi>{state.voice}</bdi>: {formatError(state.error)}
                </Typography>
            )}
        </Box>
    );
};

// ____________________________________________________________________________________________

const InworldSettings = observer(() => {
    const store = audioSettingsStore;
    const { view, loading, loadError, loadCount, savingInworld, saveInworldError } = store;
    const inworld = store.inworld;
    // The form starts from the settings that the server really uses, and starts again after its own save or a reload
    const [form, setForm] = useServerForm(inworld, formFromInworldView, loadCount);
    const [samples, setSamples] = useState(() => Object.fromEntries(LANGUAGE_KEYS.map((key) => [key, LANGUAGE_INFO[key].sample])));
    const [savedVisible, setSavedVisible] = useState(false);

    useEffect(() => {
        store.load();
    }, [store]);

    useEffect(() => {
        if (!savedVisible) return undefined;
        const timer = setTimeout(() => setSavedVisible(false), SAVED_MESSAGE_MS);
        return () => clearTimeout(timer);
    }, [savedVisible]);

    const patch = useMemo(() => (inworld && form ? buildInworldPatch(form, inworld) : {}), [inworld, form]);
    const problems = useMemo(() => (view && inworld && form ? validateInworldForm(form, view) : []), [view, inworld, form]);

    if (!view) {
        if (loadError) {
            return (
                <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
                    <Alert
                        severity="error"
                        action={<Button color="inherit" size="small" onClick={store.load} disabled={loading}>נסו שוב</Button>}
                    >
                        <AlertTitle>טעינת ההגדרות נכשלה</AlertTitle>
                        <Box dir="auto">{formatError(loadError)}</Box>
                    </Alert>
                </Box>
            );
        }
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!inworld || !form) {
        return (
            <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
                <Alert severity="warning" action={<Button color="inherit" size="small" onClick={store.load} disabled={loading}>טעינה מחדש</Button>}>
                    <AlertTitle>השרת עדיין לא עודכן</AlertTitle>
                    התשובה של GET /api/ai/settings/audio אינה כוללת את ההגדרות של Inworld (החלק inworld), ולכן אי אפשר להציג או לשמור אותן.
                </Alert>
            </Box>
        );
    }

    const { effective } = inworld;
    const { capabilities } = view;
    const models = inworldModelsOf(inworld);
    const model = models.find((entry) => entry.id === form.model);
    const range = speakingRateRangeOf(inworld);
    const rateValid = isSpeakingRateValid(form.speakingRate, inworld);
    const inworldKeyMissing = capabilities?.inworldApiKey === false;
    const paths = patchPaths(patch);
    const canSave = paths.length > 0 && problems.length === 0 && !savingInworld;
    const cost = estimateCostPerThousandFiles(model);

    // Nothing can be tested while the server has no key, no model is chosen, or the pace is not a valid number
    let testBlockedReason = '';
    if (inworldKeyMissing) testBlockedReason = 'לשרת אין מפתח Inworld';
    else if (!model) testBlockedReason = 'לא נבחר מודל';
    else if (!rateValid) testBlockedReason = 'מהירות הדיבור אינה תקינה';

    const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
    const setVoices = (key, field, value) => setForm((current) => ({ ...current, [key]: { ...current[key], [field]: value } }));

    const handleSave = async () => {
        if (await store.saveInworld(patch)) setSavedVisible(true);
    };

    /** The request of a sample: the model, the pace, the delivery (only where the model has it) and the niqqud (Hebrew only) of the form */
    const testQuery = (key) => (voice) => {
        const query = {
            text: samples[key],
            language: LANGUAGE_INFO[key].testLanguage,
            name: voice,
            model: form.model,
            speakingRate: parseSpeakingRate(form.speakingRate),
        };
        if (model?.supportsDeliveryMode) query.deliveryMode = form.deliveryMode;
        if (key === 'he') query.niqqud = form.niqqud;
        return query;
    };

    return (
        <Box dir="rtl" sx={{ p: 2, maxWidth: 1100, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom>הגדרות Inworld</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                ההגדרות האלה חלות על כל תא ב"כללי יצירה אוטומטית" שהספק שלו הוא Inworld: המודל, המהירות והקולות של כל שפה.
                לבדיקה חופשית בלי לשמור כלום, השתמשו בלשונית "בדיקת יצירה".
            </Typography>

            {/* __________ מצב נוכחי __________ */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                    <Chip
                        size="small"
                        color={inworldKeyMissing ? 'warning' : 'success'}
                        label={`מפתח Inworld בשרת: ${inworldKeyMissing ? 'חסר' : 'קיים'}`}
                    />
                    <Chip size="small" variant="outlined" label={<bdi>{`המודל הפעיל כעת: ${effective.model}`}</bdi>} />
                    <Chip size="small" variant="outlined" label={<bdi>{`מהירות: ${effective.speakingRate}`}</bdi>} />
                </Box>
                {inworldKeyMissing && <Alert severity="info" sx={{ mb: 1.5 }}>{INWORLD_KEY_NOTE} ההגדרות אפשר לשמור גם עכשיו, אבל הבדיקות יעבדו רק אחרי שהמפתח יתווסף.</Alert>}
                {effective.fellBack ? (
                    <Alert severity="warning">
                        <AlertTitle>ההגדרות השמורות במסד הנתונים אינן תקינות</AlertTitle>
                        כל עוד זה כך, היצירה האוטומטית משתמשת בערכי ברירת מחדל במקום להיכשל.
                        לחצו על "שמירה" כדי להחליף את הערכים הלא תקינים בערכים המוצגים למטה.
                        <Box component="ul" dir="ltr" sx={{ mt: 1, mb: 0, paddingInlineStart: '24px', fontSize: 13 }}>
                            {effective.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                        </Box>
                    </Alert>
                ) : (
                    <Alert severity="success">
                        ההגדרות תקינות. כל יצירה אוטומטית חדשה עם Inworld משתמשת בהן מיד.
                    </Alert>
                )}
            </Paper>

            {/* __________ מודל וסגנון __________ */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>מודל וסגנון</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="inworld-settings-model-label">מודל</InputLabel>
                            <Select
                                labelId="inworld-settings-model-label"
                                id="inworld-settings-model"
                                label="מודל"
                                value={model ? model.id : ''}
                                onChange={(e) => setField('model', e.target.value)}
                            >
                                {models.map((entry) => (
                                    <MenuItem key={entry.id} value={entry.id}>
                                        <bdi>{entry.label}</bdi>
                                        {Number.isFinite(entry.priceUsdPerMillionChars) && <> - <bdi>{`$${entry.priceUsdPerMillionChars}`}</bdi> למיליון תווים</>}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>
                                {cost !== null
                                    ? `בערך ${formatUsd(cost)} לכל 1,000 קבצים (מילה או ביטוי קצר, ${AVERAGE_CHARS_PER_FILE} תווים בממוצע; לפי מחירון Inworld ב-1.11.2026).`
                                    : ''}
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            id="inworld-settings-rate"
                            label="מהירות דיבור"
                            size="small"
                            fullWidth
                            value={form.speakingRate}
                            onChange={(e) => setField('speakingRate', e.target.value)}
                            error={!rateValid}
                            helperText={rateValid
                                ? `1 היא מהירות רגילה; פחות מ-1 מאט, יותר מ-1 מאיץ (${range.min}-${range.max}).`
                                : `מספר בין ${range.min} ל-${range.max}.`}
                            inputProps={{ inputMode: 'decimal', dir: 'ltr' }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        {model?.supportsDeliveryMode ? (
                            <FormControl size="small" fullWidth>
                                <InputLabel id="inworld-settings-delivery-label">מצב הגשה</InputLabel>
                                <Select
                                    labelId="inworld-settings-delivery-label"
                                    id="inworld-settings-delivery"
                                    label="מצב הגשה"
                                    value={INWORLD_DELIVERY_MODES.includes(form.deliveryMode) ? form.deliveryMode : ''}
                                    renderValue={(value) => <bdi>{value}</bdi>}
                                    onChange={(e) => setField('deliveryMode', e.target.value)}
                                >
                                    {INWORLD_DELIVERY_MODES.map((mode) => (
                                        <MenuItem key={mode} value={mode}><bdi>{DELIVERY_MODE_INFO[mode] || mode}</bdi></MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                מצב הגשה קיים רק במודל Inworld TTS 2 (לא ב-Flash).
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="inworld-settings-niqqud-label">ניקוד בעברית</InputLabel>
                            <Select
                                labelId="inworld-settings-niqqud-label"
                                id="inworld-settings-niqqud"
                                label="ניקוד בעברית"
                                value={INWORLD_NIQQUD_MODES.includes(form.niqqud) ? form.niqqud : ''}
                                onChange={(e) => setField('niqqud', e.target.value)}
                            >
                                {INWORLD_NIQQUD_MODES.map((mode) => (
                                    <MenuItem key={mode} value={mode}>{NIQQUD_INFO[mode] || mode}</MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>חל רק על הטקסט העברי. שם הקובץ נשאר כפי שהוא במילון.</FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Alert severity="info">{MODEL_NOTE}</Alert>
                    </Grid>
                </Grid>
            </Paper>

            {/* __________ קולות __________ */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>קולות לפי שפה</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    את שמות הקולות לוקחים מהספרייה של Inworld (או מקול משובט שנוצר שם). שם שלא קיים יחזיר שגיאה ביצירה, ואפשר לבדוק אותו כאן בכפתור "בדיקה".
                    כל לחיצה יוצרת קובץ אחד באמת ונספרת בחשבון Inworld (מילה אחת עולה שבריר אגורה). הבדיקה משתמשת בערכים שבטופס, גם לפני שמירה.
                </Typography>
                {LANGUAGE_KEYS.map((key) => {
                    const info = LANGUAGE_INFO[key];
                    const blockedReason = testBlockedReason || (samples[key].trim() ? '' : 'כתבו מילה לבדיקה');
                    return (
                        <Paper key={key} variant="outlined" sx={{ p: 2, mb: 1.5, bgcolor: 'grey.50' }}>
                            <Grid container spacing={2} alignItems="flex-start">
                                <Grid item xs={12} md={2}>
                                    <Typography sx={{ fontWeight: 'bold' }}>{info.label}</Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <VoiceCell
                                        id={`inworld-settings-${key}-female`}
                                        label="קול נשי"
                                        value={form[key].female}
                                        onChange={(value) => setVoices(key, 'female', value)}
                                        inworldView={inworld}
                                        blockedReason={blockedReason}
                                        buildQuery={testQuery(key)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <VoiceCell
                                        id={`inworld-settings-${key}-male`}
                                        label="קול גברי"
                                        value={form[key].male}
                                        onChange={(value) => setVoices(key, 'male', value)}
                                        inworldView={inworld}
                                        blockedReason={blockedReason}
                                        buildQuery={testQuery(key)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        label="מילה לבדיקה"
                                        size="small"
                                        fullWidth
                                        value={samples[key]}
                                        onChange={(e) => setSamples((current) => ({ ...current, [key]: e.target.value }))}
                                        inputProps={{ dir: 'auto', maxLength: MAX_SAMPLE_CHARS }}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    );
                })}
            </Paper>

            {/* __________ שמירה __________ */}
            <Paper
                elevation={3}
                sx={{ p: 2, position: 'sticky', bottom: 8, zIndex: 2, display: 'flex', flexDirection: 'column', gap: 1 }}
            >
                {problems.length > 0 && (
                    <Alert severity="error">
                        <Box component="ul" sx={{ m: 0, paddingInlineStart: '24px' }}>
                            {problems.map((problem) => <li key={problem}>{problem}</li>)}
                        </Box>
                    </Alert>
                )}
                {saveInworldError && (
                    <Alert severity="error">
                        <AlertTitle>השמירה נכשלה</AlertTitle>
                        <Box dir="auto">{formatError(saveInworldError)}</Box>
                    </Alert>
                )}
                {savedVisible && (
                    <Alert severity="success" onClose={() => setSavedVisible(false)}>
                        נשמר. ההגדרות החדשות חלות מיד על כל יצירה אוטומטית חדשה עם Inworld.
                    </Alert>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button variant="contained" onClick={handleSave} disabled={!canSave}>
                        {savingInworld ? 'שומר...' : 'שמירה'}
                    </Button>
                    <Button variant="outlined" onClick={() => setForm(formFromInworldView(inworld))} disabled={savingInworld || !isInworldFormChanged(form, inworld)}>
                        ביטול שינויים
                    </Button>
                    <Button variant="text" onClick={store.load} disabled={loading || savingInworld}>
                        טעינה מחדש מהשרת
                    </Button>
                    <Typography variant="body2" color="text.secondary" dir="auto">
                        {paths.length > 0 ? `יישמרו: ${paths.join(', ')}` : 'אין מה לשמור'}
                    </Typography>
                </Box>
            </Paper>

            {/* __________ פרטים טכניים __________ */}
            <Box component="details" sx={{ mt: 2, mb: 4 }}>
                <Box component="summary" sx={{ cursor: 'pointer', color: 'text.secondary' }}>פרטים טכניים: מה שמור כרגע במסד הנתונים</Box>
                <Box component="pre" dir="ltr" sx={{ fontSize: 12, overflow: 'auto', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                    {JSON.stringify(inworld.configured, null, 2)}
                </Box>
            </Box>
        </Box>
    );
});

export default InworldSettings;
